import {
  BlockReferenceContents,
  BlockReferenceNode,
  BlockReferenceSearchItemProps,
  ExegesisSlateBlock,
  ExegesisSlateChild,
  TagMentionNode,
} from '@rhizome/pgtypes';
import { BLOCK_REFERENCE_MENTION } from '@rhizome/shared-slate-utils';
import {
  createBoldPlugin,
  createItalicPlugin,
  createStrikethroughPlugin,
  createSubscriptPlugin,
  createSuperscriptPlugin,
  createUnderlinePlugin,
} from '@udecode/plate-basic-marks';
import {
  createExitBreakPlugin,
  createSoftBreakPlugin,
} from '@udecode/plate-break';
import { createComboboxPlugin } from '@udecode/plate-combobox';
import {
  createHistoryPlugin,
  createPlateEditor,
  createPlugins,
  createReactPlugin,
  PlateEditor,
  withProps,
} from '@udecode/plate-core';
import {
  createHeadingPlugin,
  ELEMENT_H1,
  ELEMENT_H2,
  ELEMENT_H3,
} from '@udecode/plate-heading';
import { createImagePlugin, ELEMENT_IMAGE } from '@udecode/plate-image';
import { createLinkPlugin } from '@udecode/plate-link';
import { createMentionPlugin, ELEMENT_MENTION } from '@udecode/plate-mention';
import { createNodeIdPlugin } from '@udecode/plate-node-id';
import {
  createParagraphPlugin,
  ELEMENT_PARAGRAPH,
} from '@udecode/plate-paragraph';
import { createResetNodePlugin } from '@udecode/plate-reset-node';
import { StyledElement } from '@udecode/plate-styled-components';
import { ImageElement } from '@udecode/plate-ui-image';
import React, { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { safeNanoid } from '../../utils/safeNanoid';
import { createSimpleBlockWrapper } from './config/BlockWrapper';
import { createPlateUI } from './config/createPlateUI';
import {
  optionsExitBreakPlugin,
  optionsResetBlockTypePlugin,
  optionsSoftBreakPlugin,
} from './config/pluginOptions';
import { CreateBlockReferenceSlateComponent } from './plugins/BlockReferenceComponent';
import { createHidePlugin } from './plugins/Hide/Hide';
import { CreateMentionSlateComponent } from './plugins/TagMentionSlateComponent';

/*
This file contains the mention component files and the preview display, to avoid circular dependencies.
In the future, may consider adopting "internal module" pattern: https://medium.com/visual-development/how-to-fix-nasty-circular-dependency-issues-once-and-for-all-in-javascript-typescript-a04c987cf0de
 
*/

export const plugins = (allowHoverPreviews: boolean, isRoot: boolean) => {
  return createPlugins<any>(
    [
      createReactPlugin(),
      createHistoryPlugin(),
      //blocks
      /*
    All blocks need to be wrapped with our generic block parent component, that provides
    the UI neccessary for linking and hiding.
    */
      createParagraphPlugin(),
      createHeadingPlugin(),
      createImagePlugin(),
      //marks
      createBoldPlugin(),
      createItalicPlugin(),
      createUnderlinePlugin(),
      createStrikethroughPlugin(),
      createSubscriptPlugin(),
      createSuperscriptPlugin(),
      createLinkPlugin(),
      createResetNodePlugin({ options: optionsResetBlockTypePlugin }),
      createSoftBreakPlugin({ options: optionsSoftBreakPlugin }),
      createExitBreakPlugin({ options: optionsExitBreakPlugin }),
      createNodeIdPlugin({ options: { idCreator: safeNanoid } }),
      createHidePlugin(),
      createComboboxPlugin(),
      createMentionPlugin<TagMentionNode>({
        options: {
          trigger: '#',
          insertSpaceAfterMention: true,
        },
      }),
      createMentionPlugin<BlockReferenceNode>({
        key: BLOCK_REFERENCE_MENTION,
        options: {
          trigger: '@',
          insertSpaceAfterMention: true,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          createMentionNode: (
            mention: BlockReferenceSearchItemProps
          ): BlockReferenceContents => {
            return {
              value: '',
              slate_parent: mention.value.slate_parent!,
              block_id: mention.value.block_id,
            };
          },
        },
      }),
    ],
    {
      components: createPlateUI({
        [ELEMENT_MENTION]: CreateMentionSlateComponent(allowHoverPreviews),
        [BLOCK_REFERENCE_MENTION]:
          CreateBlockReferenceSlateComponent(allowHoverPreviews),
        ...(isRoot
          ? {}
          : // use simple block wrappers if not root
            {
              // block elements
              [ELEMENT_H1]: createSimpleBlockWrapper(
                withProps(StyledElement, {
                  as: 'h1',
                  styles: {
                    root: css`
                      margin: 2em 0 4px;
                      font-size: 1.875rem;
                      font-weight: 500;
                      line-height: 1.5;
                    `,
                  },
                })
              ),
              [ELEMENT_H2]: createSimpleBlockWrapper(
                withProps(StyledElement, {
                  as: 'h2',
                  styles: {
                    root: css`
                      margin: 1.4em 0 1px;
                      font-size: 1.5rem;
                      font-weight: 500;
                      line-height: 1.5;
                    `,
                  },
                })
              ),
              [ELEMENT_H3]: createSimpleBlockWrapper(
                withProps(StyledElement, {
                  as: 'h3',
                  styles: {
                    root: css`
                      margin: 1em 0 1px;
                      font-size: 1.25rem;
                      font-weight: 500;
                      line-height: 1.5;
                      color: #434343;
                    `,
                  },
                })
              ),
              [ELEMENT_PARAGRAPH]: createSimpleBlockWrapper(
                withProps(StyledElement, {
                  as: 'div',
                  styles: {
                    root: css`
                      margin: 0px;
                      font-size: 1.15rem;
                      line-height: 1.5;
                    `,
                  },
                  prefixClassNames: 'p',
                })
              ),
              [ELEMENT_IMAGE]: createSimpleBlockWrapper(
                withProps(ImageElement, {
                  styles: {
                    root: {
                      userSelect: 'none',
                    },
                  },
                  align: 'left',
                  caption: {
                    disabled: true,
                  },
                })
              ),
            }),
      }),
    }
  );
};
export const mainEditorPlugins = plugins(true, true);

export const excerptEditorPlugins = createPlugins<any>(
  [
    createReactPlugin(),
    createHistoryPlugin(),
    //blocks
    /*
  All blocks need to be wrapped with our generic block parent component, that provides
  the UI neccessary for linking and hiding.
  */
    createParagraphPlugin(),
    createHeadingPlugin(),
    createImagePlugin(),
    //marks
    createBoldPlugin(),
    createItalicPlugin(),
    createUnderlinePlugin(),
    createStrikethroughPlugin(),
    createSubscriptPlugin(),
    createSuperscriptPlugin(),
    createLinkPlugin(),
    createResetNodePlugin({ options: optionsResetBlockTypePlugin }),
    // don't allow hard breaks in excerpt editor
    createSoftBreakPlugin({
      options: {
        rules: [
          { hotkey: 'shift+enter' },
          {
            hotkey: 'enter',
          },
        ],
      },
    }),

    createNodeIdPlugin({ options: { idCreator: safeNanoid } }),
    createHidePlugin(),
    createComboboxPlugin(),
    createMentionPlugin({
      options: {
        trigger: '#',
        insertSpaceAfterMention: true,
      },
    }),
    createMentionPlugin<BlockReferenceNode>({
      key: BLOCK_REFERENCE_MENTION,
      options: {
        trigger: '@',
        insertSpaceAfterMention: true,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        createMentionNode: (
          mention: BlockReferenceSearchItemProps
        ): BlockReferenceContents => {
          return {
            value: '',
            slate_parent: mention.value.slate_parent!,
            block_id: mention.value.block_id,
          };
        },
      },
    }),
  ],
  {
    components: createPlateUI({
      [ELEMENT_MENTION]: CreateMentionSlateComponent(false),
      [BLOCK_REFERENCE_MENTION]: CreateBlockReferenceSlateComponent(false),
    }),
  }
);

/*
The strategy is to take each node, read its type, find the corresponding component to render 
this type, and then render it.

Because this is for read only content, we need not worry about changes.
*/
const renderNode = (
  node: ExegesisSlateBlock | ExegesisSlateChild,
  index: number,
  editor: PlateEditor<any>
): JSX.Element | null => {
  if ('text' in node && !('type' in node)) {
    // process soft breaks
    const encodedText = encodeURI(node.text);
    const softBreaked = encodedText.split('%0A');
    // need to process text style!!
    return (
      <span key={index}>
        {softBreaked.map((txt, index) => {
          return (
            <span
              key={index}
              style={{
                textDecoration: node?.strikethrough ? 'line-through' : 'none',
                fontWeight: node?.bold ? 'bold' : 'normal',
                fontStyle: node?.italic ? 'italic' : 'normal',

                ...(node?.superscript
                  ? {
                      verticalAlign: 'super',
                      fontSize: 'smaller',
                    }
                  : {}),
                ...(node?.subscript
                  ? {
                      verticalAlign: 'sub',
                      fontSize: 'smaller',
                    }
                  : {}),
              }}
            >
              {decodeURI(txt)}
              {index === softBreaked.length - 1 ? null : <br />}
            </span>
          );
        })}
      </span>
    );
  } else {
    const type = node.type;
    // get the component corresponding to the node's type
    const component = editor.pluginsByKey[type].component;
    if (component) {
      const children =
        'children' in node
          ? node?.children.map(
              (node: ExegesisSlateBlock | ExegesisSlateChild, index) => {
                return renderNode(node, index, editor);
              }
            )
          : null;
      return React.createElement(
        component!,
        { element: { ...node }, attributes: {}, prefix: '', key: index },
        // recursively render the children
        children
      );
    } else {
      return null;
    }
  }
};

export const ReadOnlyDisplay = React.memo(function RODisplay(props: {
  nodes: ExegesisSlateBlock[];
  searchTerm?: string;
  allowPreviews?: boolean;
  isRoot: boolean;
}) {
  const selfReference = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (selfReference.current && props.isRoot) {
      const textNodes = selfReference.current.querySelectorAll('span');
      for (const node of textNodes) {
        let text = node.textContent;
        const regex = new RegExp(
          '(' +
            props?.searchTerm?.split(' ')?.reduce((str, el, ind, arr) => {
              return ind === arr.length - 1 ? str + el : str + el + '|';
            }, '') +
            ')',
          'ig'
        );
        text = text?.replace(regex, '<span class="highlight">$1</span>') || '';
        node.innerHTML = text;
      }
    }
  }, [props.isRoot, props?.searchTerm]);

  let allowPreviews = props.allowPreviews;
  if (props.allowPreviews == undefined) {
    allowPreviews = true;
  }
  const editor = createPlateEditor({
    plugins: plugins(allowPreviews || false, props.isRoot || false),
  });
  const components = props.nodes.map((node, index) => {
    return renderNode(node, index, editor);
  });
  return (
    <RODisplayStyleWrapper ref={selfReference}>
      {components}
    </RODisplayStyleWrapper>
  );
});

const RODisplayStyleWrapper = styled.div`
  .highlight {
    background-color: yellow;
  }
`;
